import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useLocalStore } from "@/lib/useLocalStore";

// Session state is persisted locally while authentication requests are handled
// by the configured API.
const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || process.env.VITE_API_URL;

function apiErrorMessage(payload, fallback) {
  if (Array.isArray(payload?.detail)) {
    return payload.detail.map((item) => item.msg).filter(Boolean).join(" ") || fallback;
  }
  return payload?.message
    || payload?.error?.message
    || payload?.error?.detail
    || (typeof payload?.error === "string" ? payload.error : null)
    || (typeof payload?.detail === "string" ? payload.detail : null)
    || fallback;
}

function sessionFromPayload(payload) {
  const data = payload?.data;
  const user = payload?.user || data?.user || null;
  const refreshToken = payload?.refreshToken || payload?.refresh_token || payload?.token?.refreshToken
    || payload?.token?.refresh_token || data?.refreshToken || data?.refresh_token || data?.tokens?.refreshToken
    || payload?.tokens?.refreshToken || null;
  const accessToken = payload?.accessToken || payload?.access_token || (typeof payload?.token === "string" ? payload.token : null)
    || payload?.token?.accessToken || payload?.token?.access_token || (typeof data?.token === "string" ? data.token : null)
    || data?.token?.accessToken || data?.token?.access_token || data?.accessToken || data?.access_token
    || data?.tokens?.accessToken || payload?.tokens?.accessToken || payload?.tokens?.access_token || null;
  return { user, accessToken, refreshToken };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useLocalStore("sr-auth", null);
  const [accessToken, setAccessToken] = useLocalStore("sr-access-token", null);
  // Refresh credentials belong in the server's HttpOnly cookie, never in
  // localStorage. This in-memory fallback only supports an API response during
  // the current tab session while the backend migrates to cookie sessions.
  const [refreshToken, setRefreshToken] = useState(null);
  const [authStatus, setAuthStatus] = useState("loading");

  const clearSession = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
  }, [setAccessToken, setRefreshToken, setUser]);

  // A persisted user alone is not proof of an active session. Validate the
  // access token first, then recover through the server-managed refresh cookie.
  useEffect(() => {
    let active = true;

    const applySession = (session) => {
      if (!active || !session.user || !session.accessToken) return false;
      setUser(session.user);
      setAccessToken(session.accessToken);
      // Kept only for backwards-compatible API responses. Production refresh
      // tokens should arrive as HttpOnly cookies and therefore remain unreadable.
      setRefreshToken(session.refreshToken || null);
      return true;
    };

    const getCurrentUser = async (token) => {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!response.ok) return null;
      let payload = null;
      try { payload = await response.json(); } catch { /* empty response */ }
      return payload?.user || payload?.data?.user || payload?.data || payload;
    };

    const restore = async () => {
      if (!API_BASE_URL) {
        if (active) setAuthStatus(user && accessToken ? "authenticated" : "unauthenticated");
        return;
      }

      try {
        if (accessToken) {
          const currentUser = await getCurrentUser(accessToken);
          if (currentUser && active) {
            setUser(currentUser);
            setAuthStatus("authenticated");
            return;
          }
        }

        const response = await fetch(`${API_BASE_URL}/auth/refresh`, { method: "POST", credentials: "include" });
        if (response.ok) {
          let payload = null;
          try { payload = await response.json(); } catch { /* empty response */ }
          const session = sessionFromPayload(payload);
          if (!session.user && session.accessToken) session.user = await getCurrentUser(session.accessToken);
          if (applySession(session)) {
            if (active) setAuthStatus("authenticated");
            return;
          }
        }
      } catch {
        // Treat an unavailable or invalid session as signed out; API requests
        // will surface their own recoverable network errors after sign-in.
      }

      if (active) {
        clearSession();
        setAuthStatus("unauthenticated");
      }
    };

    restore();
    return () => { active = false; };
    // Bootstrap exactly once; sign-in/sign-up set the status directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async ({ email, password, remember } = {}) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, remember: !!remember }),
      credentials: "include",
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "Incorrect email or password. Give it another try."));
    }

    const session = sessionFromPayload(payload);
    if (!session.user || !session.accessToken) throw new Error("We could not establish your session. Please try again.");
    setUser(session.user);
    setAccessToken(session.accessToken);
    setRefreshToken(session.refreshToken || null);
    setAuthStatus("authenticated");
    return payload;
  }, [setAccessToken, setRefreshToken, setUser]);

  const signUp = useCallback(async ({ displayName, username, email, password, confirmPassword, remember } = {}) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ displayName, username, email, password, confirmPassword, remember: !!remember }),
      credentials: "include",
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not create your account. Please try again."));
    }

    const session = sessionFromPayload(payload);
    if (!session.user || !session.accessToken) {
      throw new Error("Your account was created, but we could not sign you in. Please sign in to continue.");
    }
    setUser(session.user);
    setAccessToken(session.accessToken);
    setRefreshToken(session.refreshToken || null);
    setAuthStatus("authenticated");
    return payload;
  }, [setAccessToken, setRefreshToken, setUser]);

  const resetPassword = useCallback(async ({ token, newPassword } = {}) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPassword }),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not reset your password. Please try again."));
    }

    return payload;
  }, []);

  const updateProfile = useCallback(async ({ bio, location, website, showReadingActivity, showEmail, isPublic } = {}) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/me/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ bio, location, website, showReadingActivity, showEmail, isPublic }),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not update your profile. Please try again."));
    }

    return payload;
  }, [accessToken]);

  const getProfile = useCallback(async (username) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    if (!username) throw new Error("A profile username is required.");

    const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(username)}`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    console.log(`[Profile API] GET /users/${username} response:`, payload);

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not load your profile."));
    }

    return payload;
  }, [accessToken]);

  const followUser = useCallback(async (userId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    if (!userId) throw new Error("A user ID is required to follow someone.");

    const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(userId)}/follow`, {
      method: "POST",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API responses may not include a JSON response body.
    }

    console.log(`[Follow API] POST /users/${userId}/follow response:`, payload);

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not follow this user. Please try again."));
    }

    return payload;
  }, [accessToken]);

  const getShelves = useCallback(async () => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/me/shelves`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    console.log("[Shelves API] GET /me/shelves response:", { status: response.status, payload });

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not load your bookshelves."));
    }

    return payload;
  }, [accessToken]);

  const createShelf = useCallback(async ({ name, description, emoji, tone, isPublic = true } = {}) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/me/shelves`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ name, description, emoji, tone, isPublic }),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    console.log("[Shelves API] POST /me/shelves response:", payload);

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not create your shelf."));
    }

    return payload;
  }, [accessToken]);

  const getBooks = useCallback(async () => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/me/books`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    console.log("[Books API] GET /me/books response:", { status: response.status, payload });

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not load your books."));
    }

    return payload;
  }, [accessToken]);

  const addBookToShelf = useCallback(async ({ bookId, status, shelfId } = {}) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/me/books`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ bookId, status, shelfId }),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Some API errors may not include a JSON response body.
    }

    console.log("[Books API] POST /me/books response:", { status: response.status, payload });

    if (!response.ok) {
      throw new Error(apiErrorMessage(payload, "We could not add this book to your shelf."));
    }

    return payload;
  }, [accessToken]);

  const getBookReviews = useCallback(async (bookId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/books/${encodeURIComponent(bookId)}/reviews`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }

    console.log(`[Reviews API] GET /books/${bookId}/reviews response:`, { status: response.status, payload });
    if (!response.ok) {
      const message = response.status === 404
        ? "This book is not available in the backend yet. Search for it and add it to your bookshelf first."
        : apiErrorMessage(payload, "We could not load the reader reviews.");
      throw new Error(message);
    }
    return payload;
  }, [accessToken]);

  const createBookReview = useCallback(async (bookId, { rating, quote, body }) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/books/${encodeURIComponent(bookId)}/reviews`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ rating, quote, body }),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }

    console.log(`[Reviews API] POST /books/${bookId}/reviews response:`, { status: response.status, payload });
    if (!response.ok) {
      const message = response.status === 404
        ? "This book is not available in the backend yet. Search for it and add it to your bookshelf first."
        : apiErrorMessage(payload, "We could not post your review.");
      throw new Error(message);
    }
    return payload;
  }, [accessToken]);

  const updateBookReview = useCallback(async (reviewId, { rating, quote, body }) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/reviews/${encodeURIComponent(reviewId)}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ rating, quote, body }),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }

    console.log(`[Reviews API] PATCH /reviews/${reviewId} response:`, { status: response.status, payload });
    if (!response.ok) {
      const message = response.status === 404
        ? "This review or book was not found. Please refresh and try again."
        : apiErrorMessage(payload, "We could not update your review.");
      throw new Error(message);
    }
    return payload;
  }, [accessToken]);

  const deleteBookReview = useCallback(async (reviewId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");

    const response = await fetch(`${API_BASE_URL}/reviews/${encodeURIComponent(reviewId)}`, {
      method: "DELETE",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }

    console.log(`[Reviews API] DELETE /reviews/${reviewId} response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not delete your review."));
    return payload;
  }, [accessToken]);

  const getCommunities = useCallback(async () => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/communities`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Communities API] GET /communities response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load communities."));
    return payload;
  }, [accessToken]);

  const createCommunityApi = useCallback(async (community) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/communities`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(community),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Communities API] POST /communities response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not create the community."));
    return payload;
  }, [accessToken]);

  const getCommunityDetails = useCallback(async (communityId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/communities/${encodeURIComponent(communityId)}`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Communities API] GET /communities/${communityId} response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load this community."));
    return payload;
  }, [accessToken]);

  const joinCommunityApi = useCallback(async (communityId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/communities/${encodeURIComponent(communityId)}/join`, {
      method: "POST",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Communities API] POST /communities/${communityId}/join response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not join this community."));
    return payload;
  }, [accessToken]);

  const getCommunityEvents = useCallback(async (communityId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/communities/${encodeURIComponent(communityId)}/events`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Events API] GET /communities/${communityId}/events response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load community events."));
    return payload;
  }, [accessToken]);

  const createCommunityEvent = useCallback(async (communityId, event) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/communities/${encodeURIComponent(communityId)}/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(event),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Events API] POST /communities/${communityId}/events response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not create this event."));
    return payload;
  }, [accessToken]);

  const getCommunityMembers = useCallback(async (communityId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/communities/${encodeURIComponent(communityId)}/members`, {
      method: "GET",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Communities API] GET /communities/${communityId}/members response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load community members."));
    return payload;
  }, [accessToken]);

  const getManuscripts = useCallback(async () => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/me/manuscripts`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Manuscripts API] GET /me/manuscripts response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load your manuscripts."));
    return payload;
  }, [accessToken]);

  const getManuscript = useCallback(async (manuscriptId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/me/manuscripts/${encodeURIComponent(manuscriptId)}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Manuscripts API] GET /me/manuscripts/${manuscriptId} response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load this manuscript."));
    return payload;
  }, [accessToken]);

  const createManuscript = useCallback(async (manuscript) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/me/manuscripts`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify(manuscript),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Manuscripts API] POST /me/manuscripts response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not create your manuscript."));
    return payload;
  }, [accessToken]);

  const updateManuscript = useCallback(async (manuscriptId, updates) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/me/manuscripts/${encodeURIComponent(manuscriptId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify(updates),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Manuscripts API] PATCH /me/manuscripts/${manuscriptId} response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not update your manuscript."));
    return payload;
  }, [accessToken]);

  const deleteManuscript = useCallback(async (manuscriptId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/me/manuscripts/${encodeURIComponent(manuscriptId)}`, {
      method: "DELETE",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Manuscripts API] DELETE /me/manuscripts/${manuscriptId} response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not delete your manuscript."));
    return payload;
  }, [accessToken]);

  const createChapter = useCallback(async (manuscriptId, chapter) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/me/manuscripts/${encodeURIComponent(manuscriptId)}/chapters`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify(chapter),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Chapters API] POST /me/manuscripts/${manuscriptId}/chapters response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not create the chapter."));
    return payload;
  }, [accessToken]);

  const updateChapter = useCallback(async (chapterId, chapter) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/chapters/${encodeURIComponent(chapterId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify(chapter),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Chapters API] PATCH /chapters/${chapterId} response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not update the chapter."));
    return payload;
  }, [accessToken]);

  const deleteChapter = useCallback(async (chapterId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/chapters/${encodeURIComponent(chapterId)}`, {
      method: "DELETE",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Chapters API] DELETE /chapters/${chapterId} response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not delete the chapter."));
    return payload;
  }, [accessToken]);

  const uploadBanner = useCallback(async (file) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${API_BASE_URL}/me/banner`, {
      method: "POST",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body: formData,
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Profile API] POST /me/banner response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not upload your banner."));
    return payload;
  }, [accessToken]);

  const getCoverClubFeed = useCallback(async () => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/cover-club/feed`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Cover Club API] GET /cover-club/feed response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load the cover feed."));
    return payload;
  }, [accessToken]);

  const getCoverClubBattles = useCallback(async () => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/cover-club/battles`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Cover Club API] GET /cover-club/battles response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load cover battles."));
    return payload;
  }, [accessToken]);

  const voteCoverBattle = useCallback(async (battleId, choice) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/cover-club/battles/${encodeURIComponent(battleId)}/vote`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify({ choice }),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Cover Club API] POST /cover-club/battles/${battleId}/vote response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not record your vote."));
    return payload;
  }, [accessToken]);

  const getCoverClubCollections = useCallback(async () => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/cover-club/collections`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Cover Club API] GET /cover-club/collections response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not load cover collections."));
    return payload;
  }, [accessToken]);

  const createCoverCollection = useCallback(async ({ title, description }) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/cover-club/collections`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify({ title, description }),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log("[Cover Club API] POST /cover-club/collections response:", { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not create the collection."));
    return payload;
  }, [accessToken]);

  const addCoverCollectionItem = useCallback(async (collectionId, bookId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/cover-club/collections/${encodeURIComponent(collectionId)}/items`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify({ bookId }),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Cover Club API] POST /cover-club/collections/${collectionId}/items response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not add this cover to the collection."));
    return payload;
  }, [accessToken]);

  const deleteCoverCollectionItem = useCallback(async (collectionId, bookId) => {
    if (!API_BASE_URL) throw new Error("The API URL is not configured.");
    const response = await fetch(`${API_BASE_URL}/cover-club/collections/${encodeURIComponent(collectionId)}/items`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify({ bookId }),
    });
    let payload = null;
    try { payload = await response.json(); } catch { /* empty response */ }
    console.log(`[Cover Club API] DELETE /cover-club/collections/${collectionId}/items response:`, { status: response.status, payload });
    if (!response.ok) throw new Error(apiErrorMessage(payload, "We could not remove this cover from the collection."));
    return payload;
  }, [accessToken]);

  const signOut = useCallback(async () => {
    try {
      if (API_BASE_URL) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: refreshToken || "" }),
          credentials: "include",
        });
      }
    } finally {
      clearSession();
      setAuthStatus("unauthenticated");
    }
  }, [clearSession, refreshToken]);

  const value = useMemo(() => ({ user, accessToken, authStatus, signIn, signUp, resetPassword, getProfile, followUser, getShelves, createShelf, getBooks, addBookToShelf, getBookReviews, createBookReview, updateBookReview, deleteBookReview, getCommunities, createCommunityApi, getCommunityDetails, joinCommunityApi, getCommunityEvents, createCommunityEvent, getCommunityMembers, getManuscripts, getManuscript, createManuscript, updateManuscript, deleteManuscript, createChapter, updateChapter, deleteChapter, uploadBanner, getCoverClubFeed, getCoverClubBattles, voteCoverBattle, getCoverClubCollections, createCoverCollection, addCoverCollectionItem, deleteCoverCollectionItem, updateProfile, signOut }), [user, accessToken, authStatus, signIn, signUp, resetPassword, getProfile, followUser, getShelves, createShelf, getBooks, addBookToShelf, getBookReviews, createBookReview, updateBookReview, deleteBookReview, getCommunities, createCommunityApi, getCommunityDetails, joinCommunityApi, getCommunityEvents, createCommunityEvent, getCommunityMembers, getManuscripts, getManuscript, createManuscript, updateManuscript, deleteManuscript, createChapter, updateChapter, deleteChapter, uploadBanner, getCoverClubFeed, getCoverClubBattles, voteCoverBattle, getCoverClubCollections, createCoverCollection, addCoverCollectionItem, deleteCoverCollectionItem, updateProfile, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

export function RequireAuth({ children }) {
  const { user, authStatus } = useAuth();
  const location = useLocation();
  if (authStatus === "loading") return <div className="min-h-screen bg-cream" aria-busy="true" />;
  if (!user) return <Navigate to="/signin" replace state={{ from: location }} />;
  return children;
}
